import React from 'react'
import './TableRow.css'
import Input from '../Input/Input';
import RowKey from '../RowKey/RowKey';
import { getFormErrorByName, getFormErrorMessageByName, hasError, useFormErrorContext } from '../../../utils/context/FormErrorContext';
import Checkbox from '../Checkbox/Checkbox';
import AIRPNumberInput from '../AIRPNumberInput/AIRPNumberInput';

export interface TableCellData {
    name: string;
    type?: "text" | "float" | "integer" | "percent";
    formatType?: "currency" | "decimal" | "integer";
    header?: string;
    symbol?: string;
    forced?: boolean;
    positiveOnly?: boolean;
    inputType?: "input" | "checkbox"
    placeholder?: string;
}

interface TableRowProps<T> {
    rowLabel?: string;
    rowLabelHeader?: string
    cellData: TableCellData[];
    dataType: T;
    children?: JSX.Element
    onChange: <K extends keyof T>(name: K, value: string | boolean | number | null) => void;
    onErrorsChange?: (hasErrors: boolean) => void;
    placeholder?: string;
    paddedRow?: boolean;
    title?: string;
}

function TableRow<T>(props: TableRowProps<T>) {
    const { errors, removeError } = useFormErrorContext();

    const modifyValue = (value: string, type: string) => {
        if (type === 'integer') {
            const intValue = parseInt(value, 10);
            if (isNaN(intValue)) {
                return '';
            }
            return String(value);
        } else if (type === 'float') {
            return String(/^\d*\.?\d*$/.test(value) ? value : '');
        }

        return value;
    }

    const parseValue = (type: string, value: string | boolean | number, parse = false) => {
        const asString = String(value)

        if (asString.length === 0) {
            return null;
        }

        let decimalPlaces = 0
        switch (type) {
            case "text":
                return asString
                break;
            case "float":
                if (asString.indexOf('.') !== -1) {
                    decimalPlaces = asString.split('.')[1]?.length
                }
                return (parse ? Number(parseFloat(asString)).toFixed(decimalPlaces) : asString)
                break;
            case "integer":
                return Math.trunc(parseFloat(asString))
                break;
            default:
                return value

        }
    }

    const parseValueBlur = (type: string, value: string | boolean | number, parse = false) => {
        if (type !== "float") {
            return parseValue(type, value, parse)
        }
        const asString = String(value)

        if (asString.length === 0) {
            return null;
        }

        return (parse ? parseFloat(asString) : asString)
    }

    const createInputEle = (type: string, cell: TableCellData): JSX.Element => {
        let inputType: "text" | "number" = "text"
        let valueType: "text" | "integer" | "percent" | "float" = "text";

        if (cell.type === "float") {
            valueType = "float";
        } else if (cell.type === "integer") {
            valueType = "integer"
        }

        if (cell.type === "float" || cell.type === "integer") {
            inputType = "number"
        }

        if (cell.type === "percent") {
            valueType = "percent"
        }

        switch (valueType) {
            case "float":
                return (
                    <AIRPNumberInput
                        formatType={cell.formatType ?? "decimal"}
                        tooltip={cell.header}
                        symbol={cell.symbol}
                        textAlign='center'
                        initialValue={props.dataType[cell.name as keyof T] as number}
                        name={cell.name}
                        onChange={(value: number | null) => {
                            props.onChange(cell.name as keyof T, value)
                        }} />
                )
            case "integer":
                return (
                    <AIRPNumberInput
                        formatType="integer"
                        symbol={cell.symbol}
                        textAlign='center'
                        initialValue={props.dataType[cell.name as keyof T] as number}
                        tooltip={cell.header}
                        name={cell.name}
                        onChange={(value: number | null) => {
                            props.onChange(cell.name as keyof T, value)
                        }} />
                )
            case "percent":
                return (
                    <AIRPNumberInput
                        formatType="decimal"
                        textAlign="center"
                        name={cell.name}
                        tooltip={cell.header}
                        initialValue={props.dataType[cell.name as keyof T] as number}
                        symbol="%"
                        max={100}
                        min={-100}
                        onChange={(value: number | null) => {
                            props.onChange(cell.name as keyof T, value)
                        }} />
                )
        }


        switch (type) {
            case "input":
                return (
                    <div className={`input-container`}>
                        <Input
                            table={true}
                            name={cell.name}
                            id={cell.name}
                            label={cell.header}
                            type={inputType}
                            value={String(props.dataType[cell.name as keyof T] ?? '')}
                            onChange={(event) => {
                                removeError(getFormErrorByName(errors, cell.name)?.primaryKey as string)
                                props.onChange(
                                    cell.name as keyof T,
                                    parseValue(valueType, modifyValue((event.target as HTMLInputElement).value, inputType))
                                )
                            }
                            }
                            onBlur={(event) =>
                                props.onChange(
                                    cell.name as keyof T,
                                    parseValue(valueType, modifyValue((event.target as HTMLInputElement).value, inputType), true)
                                )}
                            forcedType={cell.forced ? cell.type : undefined}
                            positiveOnly={cell.positiveOnly}
                            placeholder={cell.placeholder}
                        />
                    </div>
                );
            case "checkbox":
                return <Checkbox
                    checked={props.dataType[cell.name as keyof T] === true}
                    onChange={(event) => {
                        removeError(getFormErrorByName(errors, cell.name)?.primaryKey as string)
                        props.onChange(cell.name as keyof T, (event.target as HTMLInputElement).checked)
                    }}
                    id={cell.name}
                    name={cell.name}
                    aria-label={cell.name}
                    checkboxclassname={hasError(errors, cell.name) ? "table-error" : ""}
                />
            default:
                return (
                    <div className={`input-container`}>
                        <Input
                            table={true}
                            name={cell.name}
                            id={cell.name}
                            label={cell.header}
                            type={inputType}
                            value={String(props.dataType[cell.name as keyof T] ?? '')}
                            onChange={(event) => {
                                removeError(getFormErrorByName(errors, cell.name)?.primaryKey as string)
                                props.onChange(
                                    cell.name as keyof T,
                                    parseValue(valueType, modifyValue((event.target as HTMLInputElement).value, inputType))
                                )
                            }
                            }
                            onBlur={(event) =>
                                props.onChange(
                                    cell.name as keyof T,
                                    parseValueBlur(valueType, modifyValue((event.target as HTMLInputElement).value, inputType), true)
                                )}
                            forcedType={cell.forced ? cell.type : undefined}
                            positiveOnly={cell.positiveOnly}
                            placeholder={cell.placeholder}
                        />
                    </div>
                );
        }
    }

    return (
        <tr className={`pier-datatable-tablerow ${props.paddedRow ? "padded-row" : ""}`}>
            {props.rowLabel &&
                <RowKey headers={props.rowLabelHeader} title={props.title}>{props.rowLabel}</RowKey>
            }
            {props.cellData.map((cell, index) => (
                <td
                    key={index}
                    className={`${cell.inputType === "input" ? "full-input-data" : "normal-input"} ${(cell.inputType === "input" || cell.inputType === undefined) ? hasError(errors, cell.name) ? " table-error" : "" : ""}`}
                    headers={cell.header}
                >
                    {createInputEle(cell.inputType ? cell.inputType : "", cell)}
                    {(cell.type === "text" && hasError(errors, cell.name)) && <div className='input-error-message-left'>{getFormErrorMessageByName(errors, cell.name)}</div>}
                </td>
            ))}
            {props.children}
        </tr>
    )
}

export default TableRow